import transformRequestHeaderArrayToObject from 'shared/utils/transformRequestHeaderArrayToObject'
import {isRequestBodyMatching, isURLMatching, isMethodMatching} from 'shared/utils/networkRequestMatching'
import getResponseTimeDifferenceInMs from './getResponseTimeDifferenceInMs'
import mergeRequestHeaders from 'shared/utils/mergeRequestHeaders'

/**
 * Adds missing header entries to the instrumented request from
 * a matching WebRequest API request - if available and recognizable.
 *
 * Network data is recorded in `network.js` and `instrumentNetwork.js`.
 * The former records data with the extension WebRequest API,
 * the latter from instrumented fetch and XMLHttpRequest methods.
 * The reason to use two data sources:
 * - WebRequest API is reliable and allows to record non-XHR requests.
 * - The instrumented methods allow to record request responses.
 *   (Firefox has this feature, but Chrome doesn't:
 *   https://bugs.chromium.org/p/chromium/issues/detail?id=487422&q=requestBody&can=2)
 *
 * Unfortunately there's no common request identifier, which is why this function compares
 * request method, URL, request body, and response time to recognize
 * the right WebRequest API for the passed instrumented request.
 * Some attributes need to be normalized / rounded for matching:
 * - The request URL can come without protocol &/ origin for the instrumented methods.
 * - The request body can have different types &/ be encoded differently
 * - The timestamp of a request's response may vary. This is because the WebRequest API
 *   and the instrumented methods don't execute at the exact same moment. The discrepancy
 *   between these data sources increases with larger response bodies and slower computer
 *   performance. This attribute is particularly challenging to manage. If we're too lenient,
 *   the user might see inaccurate data. Conversely, if we're too stringent, we might not
 *   display response bodies for requests, even when the data has been captured.
 * This function is called on the client whenever opening a network request's details.
 * While it's not elegant to repeatedly re-compute the request headers on the client,
 * the upside is that our sessions contain the recorded raw data from the different
 * sources, and we can ship changes to this algorithm, which then apply to all existing sessions,
 * in a non-destructive way.
 *
 * Good sessions in dev Hasura to test this with:
 * TODO
 */
function enrichRequestV3(request, requestsFromWebRequestAPI = [], requestsFromInstrumentation = []) {
	// It's not possible to find a WebRequest API request with headers
	// without a timestamp, or when the request is not an XHR request.
	if (!request.startedAt) return request
	if (request.type !== 'fetch' && request.type !== 'xmlhttprequest') return request
	// only consider fetch/xmlhttprequest requests from WebRequest API
	requestsFromWebRequestAPI = requestsFromWebRequestAPI.filter(request => request.type === 'xmlhttprequest')
	if (!requestsFromWebRequestAPI.length) return request

	// Filter the list of all WebRequest API requests down to the ones with
	// the same URL and request method as the instrumented request for which a match should be found.
	let matchingWebRequestAPIRequests = []
	requestsFromWebRequestAPI.forEach(requestToMatch => {
		if (
			isURLMatching(request.url, requestToMatch.url) &&
			isMethodMatching(request.method, requestToMatch.method) &&
			isRequestBodyMatching({
				requestBodyFromInstrumentation: request.requestBody,
				requestBodyFromWebRequestAPI: requestToMatch.requestBody,
			})
		) {
			requestToMatch.responseTimeDifferenceInMs = getResponseTimeDifferenceInMs(
				request.startedAt,
				requestToMatch.startedAt
			)
			matchingWebRequestAPIRequests.push(requestToMatch)
		}
	})

	if (!matchingWebRequestAPIRequests.length) return request

	// Filter the list of all instrumentation requests to all with the same URL, request method,
	// and request body as the request for which a WebRequest API match should be found.
	// These requests from the  instrumentation compete for the same WebRequest API requests for a match.
	// All requests from instrumentation need to be compared to all similar WebRequest API requests to be sure that
	// the correct matching combination is found, and not just the first one which somewhat fits.
	const competingInstrumentationRequests = requestsFromInstrumentation
		.filter(
			requestFromInstrumentation =>
				isURLMatching(requestFromInstrumentation.url, request.url) &&
				isMethodMatching(requestFromInstrumentation.method, request.method) &&
				requestFromInstrumentation.startedAt
		)
		.map(request => ({...request}))

	// Order the matching WebRequest API requests by increasing response time difference.
	matchingWebRequestAPIRequests.sort(
		(request1, request2) =>
			Math.abs(request1.responseTimeDifferenceInMs) - Math.abs(request2.responseTimeDifferenceInMs)
	)

	// Find the matching WebRequest API request with the smallest response time difference for the instrumentation
	// request, where no other instrumentation request has a smaller response time difference.
	const requestMatchFromWebRequestAPI = matchingWebRequestAPIRequests.find(matchingWebRequestAPIRequest => {
		let bestMatchingInstrumentationRequest

		for (const competingInstrumentationRequest of competingInstrumentationRequests) {
			if (
				!isRequestBodyMatching({
					requestBodyFromInstrumentation: competingInstrumentationRequest.requestBody,
					requestBodyFromWebRequestAPI: matchingWebRequestAPIRequest.requestBody,
				})
			) {
				continue
			}

			const responseTimeDifferenceInMs = getResponseTimeDifferenceInMs(
				matchingWebRequestAPIRequest.startedAt,
				competingInstrumentationRequest.startedAt
			)

			if (
				!bestMatchingInstrumentationRequest ||
				Math.abs(bestMatchingInstrumentationRequest.responseTimeDifferenceInMs) >
					Math.abs(responseTimeDifferenceInMs)
			) {
				bestMatchingInstrumentationRequest = {
					requestFromInstrumentation: competingInstrumentationRequest,
					responseTimeDifferenceInMs,
				}
			}
		}

		// check if the best matching instrumentation request is the same as the one we are currently looking at
		if (bestMatchingInstrumentationRequest?.requestFromInstrumentation.id === request.id) {
			return true
		}
		return false
	})

	// No matching WebRequest API found where no other instrumented request responded in closer time proximation.
	if (!requestMatchFromWebRequestAPI) return request

	// If the  instrumentation request has a matching WebRequest API request, merge the headers and return it
	// but only if the response time difference is within a certain threshold.
	//
	// Not setting a threshold or a too high one means that e.g. requests are mismatched when a page is rapidly reloaded,
	// or an API polled. Setting it too narrow on the other hand prevents the request from being enriched correctly.
	//
	// Usually the instrumented fetch/XMLHttpResponses come in later than the WebRequest API - only in rare cases,
	// it goes ever-so-slightly in the other direction.
	//
	// The thresholds identified are magic numbers, because the delay varies drastically depending on runtime factors
	// like the request's size, and the performance of the client computer which recorded the session.
	// The threshold is looser when the two data sources recorded the same amount of matching requests,
	// as this reduces the risk of mis-attributing requests.
	const requestCountsMatch = competingInstrumentationRequests.length === matchingWebRequestAPIRequests.length

	const MAXIMUM_ALLOWED_RESPONSE_DELAY_IN_MS = requestCountsMatch ? 750 : 175
	const MINIMUM_ALLOWED_RESPONSE_DELAY_IN_MS = -50

	const {responseTimeDifferenceInMs} = requestMatchFromWebRequestAPI
	if (
		responseTimeDifferenceInMs < MAXIMUM_ALLOWED_RESPONSE_DELAY_IN_MS &&
		responseTimeDifferenceInMs > MINIMUM_ALLOWED_RESPONSE_DELAY_IN_MS
	) {
		const originalRequestHeaders = transformRequestHeaderArrayToObject(request.requestHeaders)
		const matchingRequestHeaders = transformRequestHeaderArrayToObject(requestMatchFromWebRequestAPI.requestHeaders)
		request.requestHeaders = mergeRequestHeaders(matchingRequestHeaders, originalRequestHeaders)

		const originalResponseHeaders = transformRequestHeaderArrayToObject(request.responseHeaders)
		const matchingResponseHeaders = transformRequestHeaderArrayToObject(
			requestMatchFromWebRequestAPI.responseHeaders
		)
		request.responseHeaders = mergeRequestHeaders(matchingResponseHeaders, originalResponseHeaders)
		request.responseTimeDifferenceInMs = responseTimeDifferenceInMs
	}

	// return the request enriched (or not) with the matching WebRequest API request's response and request headers
	return request
}

export default enrichRequestV3
